import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AdminPage } from 'components';
import { H3, Loading } from 'design-system-web';
import { convertToUUID } from 'uuid-encoding';
import type { Channel } from 'lane-shared/types/ChannelType';
import type { UserType } from 'lane-shared/types/User';
import { useVisitorCustomFieldConfigs } from 'domains/visitorManagement/hooks/useVisitorCustomFieldConfigs';
import { CustomFieldDetailView } from '../visitor-configuration/tabs/customFields/CustomFieldDetailView';
import { DisableCustomFieldConfirmationModal } from '../visitor-configuration/tabs/customFields/DisableCustomFieldConfirmationModal';
import { mapQueryConfigsToDetailRows } from '../visitor-configuration/tabs/customFields/mapCustomFieldConfigRow';
import { useCustomFieldStatusUpdate } from '../visitor-configuration/tabs/customFields/useCustomFieldStatusUpdate';
import { useNavigateBackToCustomFields } from '../useNavigateBackToCustomFields';
import {
  NO_ORGANIZATION_KEY,
  CUSTOM_FIELD_CONFIG_NOT_FOUND_KEY,
} from '../visitor-configuration/tabs/customFields/customFieldsTabModelUtils';

function decodeCustomFieldId(base62Id: string): string | null {
  try {
    return convertToUUID(base62Id);
  } catch {
    return null;
  }
}

export function CustomFieldDetailPage({
  channel,
}: {
  channel?: Channel;
  user?: UserType;
}): React.ReactElement {
  const { t } = useTranslation();
  const { customFieldId: customFieldIdBase62 } = useParams<{ customFieldId: string }>();
  const navigateBack = useNavigateBackToCustomFields();
  const organizationId = channel?._id ? convertToUUID(channel._id) : null;
  const hasNavigatedRef = useRef(false);

  const customFieldUuid = useMemo(
    () => decodeCustomFieldId(customFieldIdBase62),
    [customFieldIdBase62]
  );

  const {
    data: customFieldConfigs,
    loading: configsLoading,
    called: configsCalled,
    fetch: fetchConfigs,
  } = useVisitorCustomFieldConfigs();

  useEffect(() => {
    if (!organizationId) return;

    fetchConfigs(organizationId);
  }, [organizationId, fetchConfigs]);

  const detailRows = useMemo(
    () => mapQueryConfigsToDetailRows(customFieldConfigs ?? []),
    [customFieldConfigs]
  );

  const selectedField = useMemo(
    () => (customFieldUuid ? detailRows.find(row => row.id === customFieldUuid) ?? null : null),
    [detailRows, customFieldUuid]
  );

  useEffect(() => {
    if (hasNavigatedRef.current) return;

    if (!customFieldUuid) {
      hasNavigatedRef.current = true;
      window.Toast.show(t(CUSTOM_FIELD_CONFIG_NOT_FOUND_KEY));
      navigateBack();

      return;
    }

    if (!organizationId) {
      hasNavigatedRef.current = true;
      window.Toast.show(t(NO_ORGANIZATION_KEY));
      navigateBack();

      return;
    }

    if (configsCalled && !configsLoading && !selectedField) {
      hasNavigatedRef.current = true;
      window.Toast.show(t(CUSTOM_FIELD_CONFIG_NOT_FOUND_KEY));
      navigateBack();
    }
  }, [customFieldUuid, organizationId, configsCalled, configsLoading, selectedField, navigateBack, t]);

  const {
    handleToggleStatus,
    handleDisableConfirm,
    handleDisableCancel,
    pendingDisableRow,
    isLoading,
  } = useCustomFieldStatusUpdate({ organizationId });

  return (
    <AdminPage>
      <H3 as="h1">{t('web.admin.visitorManagement.customField.detail.heading')}</H3>
      {!selectedField && (!customFieldUuid || !organizationId || !configsCalled || configsLoading) && <Loading />}
      {selectedField && (
        <CustomFieldDetailView
          config={selectedField}
          onCancel={navigateBack}
          onToggleStatus={handleToggleStatus}
          isStatusUpdating={isLoading}
        />
      )}
      <DisableCustomFieldConfirmationModal
        isOpen={!!pendingDisableRow}
        isLoading={isLoading}
        onConfirm={handleDisableConfirm}
        onCancel={handleDisableCancel}
      />
    </AdminPage>
  );
}
